import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import UserAvatar from '../components/UserAvatar';
import { summaryStats, scheduleData, attendanceChartOptions, announcements, staffTasks, parentMessages } from '../data/mockData';

const OverviewWidget = () => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-panel rounded-[2rem] p-8 bg-gradient-to-br from-blue-900/10 via-slate-900/60 to-transparent border-blue-500/20 mb-8 relative overflow-hidden"
  >
    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 blur-[100px] -mr-32 -mt-32" />
    <div className="flex flex-col lg:flex-row gap-8 items-center relative z-10">
      <div className="w-20 h-20 bg-principal rounded-3xl flex items-center justify-center text-white text-4xl shadow-2xl shadow-blue-500/40 border border-blue-400/30">
        <SafeIcon icon="FiTerminal" />
      </div>
      <div className="flex-1 text-center lg:text-left">
        <h2 className="text-2xl font-black text-chalk mb-2 tracking-tight">System Core: EduOps</h2>
        <p className="text-slate-400 text-sm leading-relaxed max-w-2xl font-medium">
          Unified operations suite for <span className="text-principal font-bold px-1.5 py-0.5 rounded bg-blue-500/10">modern academies</span>. 
          Managing student lifecycles, staff logistics, and parent engagement within a zero-friction digital environment.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4 flex-shrink-0">
        <div className="bg-slate-900/80 p-4 rounded-2xl border border-slate-800 flex flex-col items-center">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Efficiency</p>
          <p className="text-xl font-black text-emerald-400">+35%</p>
        </div>
        <div className="bg-slate-900/80 p-4 rounded-2xl border border-slate-800 flex flex-col items-center">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Up-time</p>
          <p className="text-xl font-black text-blue-400">99.9%</p>
        </div>
      </div>
    </div>
  </motion.div>
);

const MetricCard = ({ stat, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className="glass-panel rounded-3xl p-6 relative overflow-hidden group border-slate-800/50 hover:border-blue-500/30 transition-all duration-500"
  >
    <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-0 blur-3xl transition-all duration-700 group-hover:opacity-20
      ${stat.color === 'blue' ? 'bg-blue-500' : 'bg-orange-500'}
    `} />
    
    <div className="flex justify-between items-start mb-6">
      <div className={`p-4 rounded-2xl border transition-transform duration-500 group-hover:scale-110
        ${stat.color === 'blue' 
          ? 'bg-blue-500/10 text-principal border-blue-500/20' 
          : 'bg-orange-500/10 text-bell border-orange-500/20'}
      `}>
        <SafeIcon icon={stat.icon} className="text-2xl" />
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-950/50 border border-slate-800">
        {stat.trend === 'up' && <SafeIcon icon="FiArrowUpRight" className="text-emerald-400 text-xs" />}
        {stat.trend === 'down' && <SafeIcon icon="FiArrowDownRight" className="text-rose-400 text-xs" />}
        <span className={`text-[10px] font-bold ${stat.trend === 'up' ? 'text-emerald-400' : 'text-rose-400'}`}>
          {stat.change.split(' ')[0]}
        </span>
      </div>
    </div>
    
    <div>
      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{stat.title}</p>
      <h3 className="text-4xl font-black text-chalk tracking-tighter">{stat.value}</h3>
    </div>
  </motion.div>
);

const ScheduleBlock = () => (
  <div className="glass-panel rounded-[2rem] p-6 h-full flex flex-col border-slate-800/50">
    <div className="flex justify-between items-center mb-8">
      <h3 className="text-lg font-bold text-chalk flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-principal">
          <SafeIcon icon="FiCalendar" />
        </div>
        Operational Block
      </h3>
      <button className="text-[10px] uppercase font-bold text-slate-500 hover:text-principal transition-colors tracking-widest">Global View</button>
    </div>
    
    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-4">
      {scheduleData.map((item) => (
        <div key={item.id} className="relative pl-6 pb-6 border-l-2 border-slate-800 last:border-0 last:pb-0">
          <div className={`absolute left-[-9px] top-0 w-4 h-4 rounded-full border-2 border-slate-950
            ${item.status === 'completed' ? 'bg-slate-700' : 
              item.status === 'in-progress' ? 'bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'bg-blue-600'}
          `} />
          
          <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/50 hover:bg-slate-800/40 transition-all duration-300">
            <div className="flex justify-between items-start mb-3">
              <span className="text-[10px] font-mono font-bold text-principal px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20">
                {item.time}
              </span>
              {item.status === 'in-progress' && (
                <span className="text-[9px] font-black uppercase tracking-tighter text-emerald-400 flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 rounded-full">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping"></span> Live Session
                </span>
              )}
            </div>
            <h4 className="font-bold text-slate-100 mb-2 leading-tight">{item.subject}</h4>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                <SafeIcon icon="FiUser" className="text-blue-500" />
                {item.teacher}
              </div>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                <SafeIcon icon="FiMapPin" className="text-orange-500" />
                {item.room}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ParentMessages = () => (
  <div className="glass-panel rounded-[2rem] p-6 h-full flex flex-col border-slate-800/50">
    <div className="flex justify-between items-center mb-8">
      <h3 className="text-lg font-bold text-chalk flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-bell">
          <SafeIcon icon="FiMessageSquare" />
        </div>
        Parent Sync
      </h3>
      <div className="flex -space-x-2">
        {[1,2,3].map(i => (
          <div key={i} className="w-6 h-6 rounded-full border-2 border-slate-900 bg-slate-700" />
        ))}
      </div>
    </div>
    <div className="flex-1 space-y-4 overflow-y-auto custom-scrollbar pr-2">
      {parentMessages.map((msg) => (
        <div key={msg.id} className={`p-4 rounded-2xl border transition-all duration-300 cursor-pointer group
          ${msg.unread ? 'bg-blue-500/5 border-blue-500/20 shadow-lg' : 'bg-slate-950/20 border-slate-800/50 opacity-70 hover:opacity-100'}
        `}>
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-bold text-slate-200 group-hover:text-principal transition-colors">{msg.parent}</h4>
            <span className="text-[9px] font-mono text-slate-600 uppercase">{msg.time}</span>
          </div>
          <p className="text-[10px] font-bold text-slate-500 mb-3 flex items-center gap-1.5">
            <SafeIcon icon="FiUsers" className="text-[8px] text-orange-500" />
            STUDENT: {msg.student.split(' ')[0].toUpperCase()}
          </p>
          <p className="text-sm text-slate-400 line-clamp-2 leading-relaxed italic">"{msg.message}"</p>
        </div>
      ))}
    </div>
  </div>
);

export default function Dashboard() {
  return (
    <div className="space-y-8 pb-20">
      {/* Header Section */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 mb-10">
        <div>
          <motion.h1 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-black text-chalk tracking-tighter"
          >
            Command Center
          </motion.h1>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1.5 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest">Core Stable</span>
            </div>
            <span className="text-slate-600 text-xs font-mono">NODE_ID: OPS-HQ-01</span>
          </div>
        </div>
        
        <div className="flex gap-4 w-full xl:w-auto">
          <button className="flex-1 xl:flex-none glass-panel px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all text-slate-400 border-slate-800 flex items-center justify-center gap-2">
            <SafeIcon icon="FiDownload" /> Export Audit
          </button>
          <button className="flex-1 xl:flex-none bg-principal text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest shadow-xl shadow-blue-500/20 hover:shadow-blue-500/40 transition-all flex items-center justify-center gap-2">
            <SafeIcon icon="FiSettings" /> Module Config
          </button>
        </div>
      </div>

      <OverviewWidget />

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryStats.map((stat, index) => (
          <MetricCard key={stat.id} stat={stat} index={index} />
        ))}
      </div>

      {/* Middle Row: Schedule & Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 min-h-[500px]">
        <div className="lg:col-span-1">
          <ScheduleBlock />
        </div>
        <div className="lg:col-span-2">
          <div className="glass-panel rounded-[2rem] p-8 h-full flex flex-col border-slate-800/50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <h3 className="text-lg font-bold text-chalk flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-principal">
                  <SafeIcon icon="FiActivity" />
                </div>
                Attendance Analytics
              </h3>
              <div className="flex gap-2">
                {['Daily', 'Weekly', 'Monthly'].map(t => (
                  <button key={t} className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${t === 'Weekly' ? 'bg-principal text-white' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-h-[300px] w-full">
              <ReactECharts option={attendanceChartOptions} style={{ height: '100%', width: '100%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 min-h-[400px]">
        <div className="xl:col-span-1">
          <ParentMessages />
        </div>
        <div className="glass-panel rounded-[2rem] p-6 h-full border-slate-800/50">
          <h3 className="text-lg font-bold text-chalk mb-8 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
              <SafeIcon icon="FiCheckSquare" />
            </div>
            Priority Tasks
          </h3>
          <div className="space-y-3">
            {staffTasks.map(task => (
              <div key={task.id} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-950/40 border border-slate-800/50 hover:border-slate-700 transition-all cursor-pointer group">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${task.status === 'Completed' ? 'bg-principal border-principal text-white' : 'border-slate-800 group-hover:border-principal'}`}>
                  {task.status === 'Completed' && <SafeIcon icon="FiCheck" className="text-xs" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold truncate ${task.status === 'Completed' ? 'text-slate-600 line-through' : 'text-slate-200'}`}>{task.task}</p>
                  <p className="text-[10px] font-mono text-slate-500 mt-1 uppercase">{task.assignee}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="glass-panel rounded-[2rem] p-6 h-full border-slate-800/50">
          <h3 className="text-lg font-bold text-chalk mb-8 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-bell">
              <SafeIcon icon="FiRadio" />
            </div>
            Broadcasts
          </h3>
          <div className="space-y-4">
            {announcements.map(item => (
              <div key={item.id} className="relative pl-4 border-l-2 border-orange-500/30 py-1">
                <div className="flex justify-between items-start mb-1">
                  <span className={`text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded ${item.priority === 'high' ? 'bg-orange-500/20 text-bell' : 'bg-slate-800 text-slate-400'}`}>
                    {item.type}
                  </span>
                  <span className="text-[9px] font-mono text-slate-600">{item.date}</span>
                </div>
                <p className="text-sm font-bold text-slate-200">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}